import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL?.trim()
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY?.replace(/\s/g, '')

if (!supabaseUrl || !supabaseKey) {
  console.error('Missing Supabase environment variables!')
}

// Custom fetch that strips abort signals to prevent the Supabase client's
// internal AbortController from cancelling data queries during auth state changes.
const fetchWithoutAbort = (url, options = {}) => {
  const { signal, ...rest } = options
  return fetch(url, rest)
}

export const supabase = createClient(supabaseUrl, supabaseKey, {
  global: { fetch: fetchWithoutAbort }
})

// Patch getSession to catch AbortErrors from the GoTrueClient's internal
// AbortController. Every data query calls getSession() to get the access token,
// and if the internal AbortController is in a broken state, it aborts getSession()
// which cascades into failing ALL .from() queries.
// When getSession throws, we fall back to reading the session from localStorage
// so that data queries can still use the user's JWT for RLS.
export const STORAGE_KEY = `sb-${new URL(supabaseUrl).hostname.split('.')[0]}-auth-token`
const _originalGetSession = supabase.auth.getSession.bind(supabase.auth)
supabase.auth.getSession = async function(...args) {
  try {
    return await _originalGetSession(...args)
  } catch (err) {
    if (err?.name === 'AbortError' || err?.message?.includes('AbortError')) {
      // Fall back to localStorage session
      try {
        const stored = localStorage.getItem(STORAGE_KEY)
        if (stored) {
          const session = JSON.parse(stored)
          return { data: { session }, error: null }
        }
      } catch { /* ignore parse errors */ }
      return { data: { session: null }, error: null }
    }
    throw err
  }
}

// Check if an error is an AbortError (from Supabase client internal AbortController)
export function isAbortError(err) {
  return err?.name === 'AbortError' || err?.message?.includes('AbortError')
}

// Edge Function helper
export async function callEdgeFunction(name, body) {
  let { data: { session } } = await supabase.auth.getSession()

  // If access token is expired (or expiring within 30s), refresh it directly
  // via the Supabase auth API. We bypass supabase.auth.refreshSession() because
  // the AbortController bug also affects that method — it throws and the catch
  // silently ignores the error, leaving us with the expired token. A direct
  // fetch to the auth endpoint is unaffected by the AbortController bug.
  if (session?.access_token) {
    try {
      const payload = JSON.parse(atob(session.access_token.split('.')[1]))
      if (payload.exp * 1000 < Date.now() + 30000) {
        const stored = (() => {
          try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') } catch { return {} }
        })()
        if (stored?.refresh_token) {
          const resp = await fetch(`${supabaseUrl}/auth/v1/token?grant_type=refresh_token`, {
            method: 'POST',
            headers: { apikey: supabaseKey, 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh_token: stored.refresh_token }),
          })
          if (resp.ok) {
            const refreshed = await resp.json()
            if (refreshed?.access_token) {
              // Persist refreshed tokens so future calls use them too
              localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...stored, ...refreshed }))
              session = { ...session, access_token: refreshed.access_token }
            }
          }
        }
      }
    } catch { /* ignore decode/refresh errors, proceed with existing token */ }
  }

  const response = await fetch(
    `${supabaseUrl}/functions/v1/${name}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session?.access_token || supabaseKey}`,
        apikey: supabaseKey,
      },
      body: JSON.stringify(body),
    }
  )
  const data = await response.json()
  if (!response.ok) {
    throw new Error(data.error || data.message || `Edge function ${name} failed`)
  }
  return data
}

// Database helper functions
export const db = {
  // Create a new respondent
  async createRespondent(data) {
    const { data: respondent, error } = await supabase
      .from('respondents')
      .insert([{
        name: data.name,
        email: data.email,
        partner_name: data.partnerName,
        partner_email: data.partnerEmail,
        biological_sex: data.biologicalSex,
        age: data.age,
        ...(data.userId && { user_id: data.userId }),
        ...(data.clientPairId && { client_pair_id: data.clientPairId }),
      }])
      .select()
      .single()

    if (error) throw error
    return respondent
  },

  // Save profile responses
  async saveProfileResponses(respondentId, responses) {
    const records = Object.entries(responses).map(([questionId, answer]) => ({
      respondent_id: respondentId,
      question_id: questionId,
      answer: Array.isArray(answer) ? answer.join(', ') : answer,
    }))

    const { error } = await supabase
      .from('profile_responses')
      .insert(records)

    if (error) throw error
  },

  // Save likert scale responses
  async saveLikertResponses(respondentId, responses) {
    const records = Object.entries(responses).map(([questionId, score]) => {
      // Extract group_id from question_id (e.g., "dci1_1" -> "dci1")
      const groupId = questionId.replace(/_\d+$/, '')
      return {
        respondent_id: respondentId,
        group_id: groupId,
        question_id: questionId,
        score: parseInt(score),
      }
    })

    const { error } = await supabase
      .from('likert_responses')
      .insert(records)

    if (error) throw error
  },

  // Mark survey as completed
  async completeRespondent(respondentId) {
    const { error } = await supabase
      .from('respondents')
      .update({ completed_at: new Date().toISOString() })
      .eq('id', respondentId)

    if (error) throw error
  },

  // Submit entire survey
  async submitSurvey(formData, options = {}) {
    const { userId, clientPairId } = options

    // Extract profile data
    const profileData = {
      name: formData.q1,
      email: formData.q2,
      biologicalSex: formData.q3,
      age: parseInt(formData.q4),
      partnerName: formData.q5,
      partnerEmail: formData.q6,
      ...(userId && { userId }),
      ...(clientPairId && { clientPairId }),
    }

    // Create respondent
    const respondent = await this.createRespondent(profileData)

    // Separate profile vs likert responses
    const profileResponses = {}
    const likertResponses = {}

    Object.entries(formData).forEach(([key, value]) => {
      if (key.startsWith('q')) {
        profileResponses[key] = value
      } else if (key.startsWith('dci') || key.startsWith('pi')) {
        likertResponses[key] = value
      }
    })

    // Save all responses
    await this.saveProfileResponses(respondent.id, profileResponses)
    await this.saveLikertResponses(respondent.id, likertResponses)
    await this.completeRespondent(respondent.id)

    // Notify facilitator if both clients are now done (non-blocking)
    if (clientPairId) {
      callEdgeFunction('notify-facilitator-completion', { client_pair_id: clientPairId })
        .catch((err) => console.warn('notify-facilitator-completion failed (non-fatal):', err))
    }

    return respondent
  },

  // Check if email already submitted
  async checkExistingSubmission(email) {
    const { data, error } = await supabase
      .from('respondents')
      .select('id, completed_at')
      .eq('email', email)
      .single()

    if (error && error.code !== 'PGRST116') throw error // PGRST116 = no rows
    return data
  },

  // Admin: Get all completed submissions with summary scores
  async getSubmissions() {
    const { data, error } = await supabase
      .from('respondent_summary')
      .select('*')
      .order('completed_at', { ascending: false })

    if (error) throw error
    return data
  },

  // Admin: Get a single submission with all responses
  async getSubmissionById(respondentId) {
    const { data: respondent, error: rError } = await supabase
      .from('respondents')
      .select('*')
      .eq('id', respondentId)
      .single()

    if (rError) throw rError

    const { data: profileResponses, error: pError } = await supabase
      .from('profile_responses')
      .select('*')
      .eq('respondent_id', respondentId)
      .order('question_id')

    if (pError) throw pError

    const { data: likertResponses, error: lError } = await supabase
      .from('likert_responses')
      .select('*')
      .eq('respondent_id', respondentId)
      .order('question_id')

    if (lError) throw lError

    return { respondent, profileResponses, likertResponses }
  },

  // Admin: Delete a submission and all related data (CASCADE)
  async deleteSubmission(respondentId) {
    const { error } = await supabase
      .from('respondents')
      .delete()
      .eq('id', respondentId)

    if (error) throw error
    return true
  },

  // Admin: Get couple data (both partners with all responses)
  async getCoupleData(respondentId) {
    // Fetch the respondent
    const { data: person1, error: e1 } = await supabase
      .from('respondents')
      .select('*')
      .eq('id', respondentId)
      .single()

    if (e1) throw e1
    if (!person1.partner_id) throw new Error('No linked partner found')

    // Fetch the partner
    const { data: person2, error: e2 } = await supabase
      .from('respondents')
      .select('*')
      .eq('id', person1.partner_id)
      .single()

    if (e2) throw e2

    // Fetch all responses for both
    const [p1Profile, p1Likert, p2Profile, p2Likert] = await Promise.all([
      supabase.from('profile_responses').select('*').eq('respondent_id', person1.id).order('question_id'),
      supabase.from('likert_responses').select('*').eq('respondent_id', person1.id).order('question_id'),
      supabase.from('profile_responses').select('*').eq('respondent_id', person2.id).order('question_id'),
      supabase.from('likert_responses').select('*').eq('respondent_id', person2.id).order('question_id'),
    ])

    if (p1Profile.error) throw p1Profile.error
    if (p1Likert.error) throw p1Likert.error
    if (p2Profile.error) throw p2Profile.error
    if (p2Likert.error) throw p2Likert.error

    return {
      person1: { respondent: person1, profileResponses: p1Profile.data, likertResponses: p1Likert.data },
      person2: { respondent: person2, profileResponses: p2Profile.data, likertResponses: p2Likert.data },
    }
  },

  // Admin: Get all facilitators
  async getFacilitators() {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('role', 'facilitator')
      .order('created_at', { ascending: false })

    if (error) throw error
    return data
  },

  // Admin: Get facilitator detail with their client pairs
  async getFacilitatorDetail(facilitatorId) {
    const { data: facilitator, error: fError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', facilitatorId)
      .eq('role', 'facilitator')
      .single()

    if (fError) throw fError

    const { data: pairs, error: pError } = await supabase
      .from('client_pairs')
      .select('*')
      .eq('facilitator_id', facilitatorId)
      .order('created_at', { ascending: false })

    if (pError) throw pError

    return { facilitator, pairs }
  },

  // Facilitator: Get own client pairs
  async getClientPairs() {
    const { data, error } = await supabase
      .from('client_pairs')
      .select('*')
      .order('created_at', { ascending: false })

    if (error) throw error
    return data
  },

  // Facilitator: Get single client pair detail
  async getClientPairDetail(pairId) {
    const { data, error } = await supabase
      .from('client_pairs')
      .select('*')
      .eq('id', pairId)
      .single()

    if (error) throw error
    return data
  },

  // Client: Get own client pair
  async getMyClientPair(userId) {
    const { data, error } = await supabase
      .from('client_pairs')
      .select('*')
      .or(`male_client_id.eq.${userId},female_client_id.eq.${userId}`)
      .single()

    if (error && error.code !== 'PGRST116') throw error
    return data
  },

  // Client: Get own respondent record
  async getMyRespondent(userId) {
    const { data, error } = await supabase
      .from('respondents')
      .select('*')
      .eq('user_id', userId)
      .single()

    if (error && error.code !== 'PGRST116') throw error
    return data
  },

  // Save or update survey draft (upsert by user_id)
  async saveDraft(userId, clientPairId, formData, currentStep) {
    const { error } = await supabase
      .from('survey_drafts')
      .upsert({
        user_id: userId,
        client_pair_id: clientPairId,
        form_data: formData,
        current_step: currentStep,
      }, {
        onConflict: 'user_id',
      })

    if (error) throw error
  },

  // Load existing draft for the current user
  async getDraft(userId) {
    const { data, error } = await supabase
      .from('survey_drafts')
      .select('*')
      .eq('user_id', userId)
      .single()

    if (error && error.code !== 'PGRST116') throw error
    return data
  },

  // Delete draft after final submission
  async deleteDraft(userId) {
    const { error } = await supabase
      .from('survey_drafts')
      .delete()
      .eq('user_id', userId)

    if (error) throw error
  },

  // Admin: Get all contact submissions
  async getContactSubmissions() {
    const { data, error } = await supabase
      .from('contact_submissions')
      .select('*')
      .order('created_at', { ascending: false })

    if (error) throw error
    return data
  },

  // Admin: Get pending facilitator count
  async getPendingFacilitatorCount() {
    const { count, error } = await supabase
      .from('profiles')
      .select('id', { count: 'exact', head: true })
      .eq('role', 'facilitator')
      .eq('status', 'pending_approval')

    if (error) throw error
    return count
  },

  // Admin: Get unread contact submission count
  async getUnreadContactCount() {
    const { count, error } = await supabase
      .from('contact_submissions')
      .select('id', { count: 'exact', head: true })
      .eq('is_read', false)

    if (error) throw error
    return count
  },

  // Admin: Mark a contact submission as read
  async markContactRead(id) {
    const { error } = await supabase
      .from('contact_submissions')
      .update({ is_read: true })
      .eq('id', id)

    if (error) throw error
  },

  // Admin: Delete a contact submission
  async deleteContactSubmission(id) {
    const { error } = await supabase
      .from('contact_submissions')
      .delete()
      .eq('id', id)

    if (error) throw error
  },

  // Update own profile (non-email fields, RLS: Users update own profile)
  async updateProfile(userId, updates) {
    const ALLOWED = ['salutation', 'full_name', 'organization_name', 'organization_address', 'professional_role', 'professional_role_other']
    const safe = Object.fromEntries(Object.entries(updates).filter(([k]) => ALLOWED.includes(k)))
    const { error } = await supabase
      .from('profiles')
      .update(safe)
      .eq('id', userId)

    if (error) throw error
  },

  // Accept privacy consent (sets privacy_accepted_at for the current user)
  async acceptPrivacyConsent(userId) {
    const { error } = await supabase
      .from('profiles')
      .update({ privacy_accepted_at: new Date().toISOString() })
      .eq('id', userId)

    if (error) throw error
  },

  // Update user's preferred language
  async updateLanguagePreference(userId, lang) {
    const { error } = await supabase
      .from('profiles')
      .update({ preferred_language: lang })
      .eq('id', userId)
    if (error) throw error
  },

  // Admin: Update any facilitator's profile (non-email fields, RLS: Admins update all profiles)
  async updateFacilitatorProfile(facilitatorId, updates) {
    const ALLOWED = ['salutation', 'full_name', 'organization_name', 'organization_address', 'professional_role', 'professional_role_other']
    const safe = Object.fromEntries(Object.entries(updates).filter(([k]) => ALLOWED.includes(k)))
    const { error } = await supabase
      .from('profiles')
      .update(safe)
      .eq('id', facilitatorId)

    if (error) throw error
  },

  // Set pending_email after self-service email change request
  async setPendingEmail(userId, pendingEmail) {
    const { error } = await supabase
      .from('profiles')
      .update({ pending_email: pendingEmail })
      .eq('id', userId)

    if (error) throw error
  },

  // Link partners after both submit
  async linkPartners(email1, email2) {
    const { data: respondents, error } = await supabase
      .from('respondents')
      .select('id, email')
      .in('email', [email1, email2])

    if (error) throw error
    if (respondents.length !== 2) return null

    const [r1, r2] = respondents
    
    await supabase
      .from('respondents')
      .update({ partner_id: r2.id })
      .eq('id', r1.id)

    await supabase
      .from('respondents')
      .update({ partner_id: r1.id })
      .eq('id', r2.id)

    return true
  }
}
